import {
  Input as HeadlessUIInput,
  InputProps as HeadlessUIInputProps,
} from "@headlessui/react";
import {
  ForwardedRef,
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  SVGProps,
} from "react";
import tw, { css, styled } from "twin.macro";

type IconType = ForwardRefExoticComponent<
  Omit<SVGProps<SVGSVGElement>, "ref"> & {
    title?: string | undefined;
    titleId?: string | undefined;
  } & RefAttributes<SVGSVGElement>
>;

const commonInputIconStyles = css([tw`h-5 w-5 text-gray-400`]);

const AddOnElement = styled.span(
  tw`flex select-none items-center text-gray-500 sm:text-sm`
);

const IconWrapperElement = styled.div(
  tw`pointer-events-none absolute inset-y-0 flex items-center`
);

const InputElement = styled(HeadlessUIInput)([
  tw`flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:(text-sm leading-6)`,

  // Hide arrows from number inputs
  tw`[&::-webkit-inner-spin-button]:([-webkit-appearance: none] m-0) [&::-webkit-outer-spin-button]:([-webkit-appearance: none] m-0) [&[type=number]]:([appearance: textfield])`,
]);

export interface InputProps extends HeadlessUIInputProps {
  addOnStart?: string;
  iconStart?: IconType;
  addOnEnd?: string;
  iconEnd?: IconType;
}

const Input = forwardRef(
  (
    {
      addOnStart,
      addOnEnd,
      iconStart: IconStart,
      iconEnd: IconEnd,
      ...props
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    return (
      <div
        css={[
          tw`relative rounded-md shadow-sm bg-white ring-1 ring-inset ring-slate-900/10 text-gray-900 placeholder:text-gray-400 sm:(text-sm leading-6) focus-within:(ring-2 ring-primary-800)`,
          /** @deprecated use data-invalid rather than aria-invalid */
          props["aria-invalid"] && tw`ring-red-500 focus-within:(ring-red-500)`,
          tw`has-[input[data-disabled]]:bg-gray-200`,
          tw`has-[input[data-invalid]]:(ring-red-500 focus-within:(ring-red-500))`,
        ]}
        tw="flex"
      >
        {addOnStart && <AddOnElement tw="pl-3">{addOnStart}</AddOnElement>}
        {/* {!addOnStart && IconStart && (
          <IconWrapperElement tw="left-0 pl-3">
            <IconStart css={commonInputIconStyles} aria-hidden="true" />
          </IconWrapperElement>
        )} */}
        <InputElement
          {...props}
          ref={ref}
          css={[
            !addOnStart && IconStart && tw`pl-10`,
            !addOnEnd && IconEnd && tw`pr-10`,
          ]}
        />
        {addOnEnd && <AddOnElement tw="pr-3">{addOnEnd}</AddOnElement>}
        {!addOnEnd && IconEnd && (
          <IconWrapperElement tw="right-0 pr-3">
            <IconEnd css={commonInputIconStyles} aria-hidden="true" />
          </IconWrapperElement>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
